import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UserService } from '../../../security/users/services/user.service';
import { environment } from '../../../../environments/environment';
import { BlockBlobClient } from '@azure/storage-blob';
import { firstValueFrom, from, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AttachmentService } from '../../services/attachment.service';

@Component({
  selector: 'app-attachment',
  templateUrl: './attachment.component.html',
  styleUrl: './attachment.component.scss'
})
export class AttachmentComponent implements OnInit, OnChanges {
  @Input() clear = false;
  @Input() allowMultiple = true;
  @Input() accept: string[] = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.mp3', '.mp4', '.avi', '.mov'];
  @Output() onFilesUploaded = new EventEmitter<Array<{ remoteFileName: string, OriginalFileName: string, metadataId: number }>>();

  attachments: Array<{ file: File; url: string, uploadProgress: number, uploadError?: boolean, metadataId?: number }> = [];
  progress = 0
  filesEndpoint = `${environment.apiUrl}/Blobs`;


  constructor(
    private http: HttpClient,
    private _userService: UserService,
    private attachmentService: AttachmentService) { }

  ngOnInit(): void {
    this.attachments = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('Changes detected: old value -> current value ', `${changes['clear']?.previousValue} -> ${changes['clear']?.currentValue}`);
    if (changes['clear'] && changes['clear'].currentValue) {
      console.log('Clearing attachments due to input change');
      this.attachments = [];
    }
  }

  async onSelectedFileChange(event: any) {
    if (!event.target || !event.target.files) {
      console.error('No files selected or event target is undefined');
      return;
    }

    if (event.target.files.length === 0) {
      console.error('No files selected');
      return;
    }

    this.attachments = [...this.attachments, ...(Array.from(event.target.files) as File[])
      .filter(file => file.size > 0 && this.accept.includes(`.${file.name.split('.').pop()?.toLowerCase()}`))
      .map(file => ({
        file,
        url: '',
        uploadProgress: 0,
        uploadError: false,
        metadataId: 0
      }))];

    const tobeUploaded = this.attachments.filter(attachment => attachment.metadataId === 0);

    if (tobeUploaded.length === 0) {
      console.error('No valid files selected');
      return;
    }

    const uploadPromises = tobeUploaded
      .map(async attachment => {
        try {
          const uploadInfo = await firstValueFrom(
            this.getFileUploadToken(this._userService.currentUser.id, `${uuidv4()}.${attachment.file.name.split('.').pop() || ''}`)
          );
          const result = await this.uploadFile(uploadInfo, attachment.file);
          attachment.url = result;
          console.log(`File ${attachment.file.name} uploaded to ${attachment.url}`);
        } catch (err) {
          console.error(`Failed to upload ${attachment.file.name}`, err);
        }
      });

    await Promise.allSettled(uploadPromises);
    const uploadedFiles = this.attachments
      .filter(attachment => attachment.uploadProgress === 1 && !attachment.uploadError)
      .map(attachment => ({
        remoteFileName: (attachment.url.split('/').pop() || '').split('?')[0],
        OriginalFileName: attachment.file.name,
        metadataId: attachment.metadataId || 0
      }));
    this.onFilesUploaded.emit(uploadedFiles);

    if (event.target && event.target.value) {
      event.target.value = '';
    }
  }

  shortFileNameWithExt(attachment: { file: File; url: string }): string {
    const maxLength = 20;
    const ext = attachment.file.name.split('.').pop();
    const nameWithoutExt = attachment.file.name.substring(0, attachment.file.name.length - ext.length - 1);

    if (nameWithoutExt.length > maxLength) {
      return nameWithoutExt.substring(0, maxLength - 3) + '...' + '.' + ext;
    }
    return attachment.file.name;
  }

  removeFile(attachment: { file: File; url: string, uploadProgress: number, uploadError?: boolean, metadataId?: number }) {
    from(this.attachmentService.removeUploadingFile(attachment.url))
      .pipe(switchMap(() => this.attachmentService.deleteFileMetadata(attachment.metadataId)))
      .subscribe({
        next: () => {
          console.log(`Attachment metadata for ${attachment.file.name} removed successfully.`);
          const idx = this.attachments.findIndex(a => a.file.name === attachment.file.name);
          if (idx === -1) {
            console.warn(`Attachment for file ${attachment.file.name} not found.`);
            return;
          }
          if (idx > -1) {
            this.attachments.splice(idx, 1);
            const uploadedFiles = this.attachments
              .filter(attachment => attachment.uploadProgress === 1 && !attachment.uploadError)
              .map(attachment => ({
                remoteFileName: (attachment.url.split('/').pop() || '').split('?')[0],
                OriginalFileName: attachment.file.name,
                metadataId: attachment.metadataId || 0
              }));
            this.onFilesUploaded.emit(uploadedFiles)
          }
        },
        error: (error) => {
          console.error(`Error removing attachment metadata for ${attachment.file.name}:`, error);
        }
      });
  }

  getFileUploadToken(userId: string, fileName: string) {
    return this.http
      .get<{ uploadUrl: string; blobName: string }>(
        `${this.filesEndpoint}/write-delete-sas`,
        { params: { userId, fileName } }
      );
  }

  getFileReadTokenUrl(blobName: string) {
    return this.http
      .get<{ readSasUri: string; blobName: string, expiresOn: string }>(
        `${this.filesEndpoint}/read-sas`,
        { params: { blobName } }
      );
  }

  async uploadFile(uploadInfo: { uploadUrl: string; blobName: string; }, file: File) {
    const blobClient = new BlockBlobClient(uploadInfo.uploadUrl);
    const tobeUploaded = this.attachments.filter(attachment => attachment.metadataId === 0);
    const idx = tobeUploaded.findIndex(a => a.file.name === file.name);

    try {
      await blobClient.uploadData(file, {
        blockSize: 4 * 1024 * 1024,  // 4 MiB
        concurrency: 2,              // two parallel blocks
        blobHTTPHeaders: {
          blobContentType: file.type  // e.g. "image/jpeg"
        },
        onProgress: (ev) => {
          console.log(`Progress: ${ev.loadedBytes} bytes uploaded of ${file.size} bytes`);

          if (idx > -1) {
            tobeUploaded[idx].uploadProgress = ev.loadedBytes / file.size;
            this.attachments = [...this.attachments]; // trigger change detection
          }
        }
      });

      const uploadResult: any = await firstValueFrom(this.save(uploadInfo, file));
      console.log(`File ${file.name} uploaded successfully. Result:`, uploadResult);

      if (idx > -1) {
        console.log(this.attachments[idx]);

        tobeUploaded[idx].uploadProgress = 1;
        tobeUploaded[idx].metadataId = uploadResult.Id;
        this.attachments = [...this.attachments];
        console.log(`Attachment metadata updated for file ${file.name}:`, tobeUploaded[idx]);
      }
      return uploadInfo.uploadUrl;
    }
    catch (error) {
      if (idx > -1) {
        tobeUploaded[idx].uploadError = true;
        tobeUploaded[idx].uploadProgress = 0;
        tobeUploaded[idx].metadataId = undefined;
        this.attachments = [...this.attachments];
      }
      console.error(`Error uploading file ${file.name}:`, error);
      throw error;
    }
  }

  save(uploadInfo: { uploadUrl: string; blobName: string; }, file: File) {
    return this.http.post(this.filesEndpoint, {
      FileUrl: uploadInfo.uploadUrl.split('?')[0],
      FileName: uploadInfo.blobName,
      OriginalFileName: file.name,
      FileType: file.type,
      FileSize: file.size,
      OwnerId: this._userService.currentUser.id
    })
  }

  sanitizeAndShortenFileName(fileName: string): string {
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const shortenedFileName = sanitizedFileName.replace(/_{2,}/g, '_');
    return shortenedFileName.length > 50 ? shortenedFileName.substring(0, 50) : shortenedFileName;
  }
}

/**
 * TODO:
 * 1. An attachment removal should 
 *   - Delete the blob db record in addition to removing the file from the server/Azure Blob Storage
 *   - Update the parent component with the attachments list
 * 2. Add a confirmation dialog before removing an attachment
 * 3. Disable file selection while upload to Azure Blob Storage is in progress?
 */